/**
 * Agents API Client
 *
 * Handles all agent registry API calls for workflow designer
 * Uses centralized apiClient for consistent API access
 *
 * Follows: .equilateral-standards/serverless-saas-aws/api_standards.md
 */

import { Make_Authorized_API_Call, type TimComboAPIResponse } from './apiClient';
import { API_ENDPOINTS } from '../services/api/endpoints';

// Re-export response types for consumers
export type { TimComboAPIResponse };

/**
 * Workflow Agent interface for workflow designer palette
 */
export interface WorkflowAgent {
  id: string;
  name: string;
  description: string;
  category: 'data' | 'ai' | 'integration' | 'notification' | 'control' | 'validation' | 'compliance' | 'security' | 'privacy' | 'deployment' | 'testing' | 'analysis' | 'automation' | 'monitoring' | 'optimization' | 'business';
  icon: string;
  color: string;
  estimatedCost: number;
  avgDuration: number;
  successRate: number;
  capabilities?: string[];
  pricing?: {
    type: string;
    amount: number;
    currency: string;
  };
}

/**
 * Capability Pack interface for pre-configured agent workflows
 */
export interface CapabilityPack {
  id: string;
  name: string;
  description: string;
  category: string;
  agentCount: number;
  estimatedCost: number;
  estimatedDuration: number;
  successRate: number;
  slashCommand: string;
  agents: {
    id: string;
    name: string;
    description: string;
  }[];
  features: string[];
  pricing?: {
    monthly: number;
    currency: string;
  };
}

/**
 * Agent Method interface for detailed agent capabilities
 */
export interface AgentMethod {
  name: string;
  description: string;
  category?: string;
  inputs?: {
    name: string;
    type: string;
    required: boolean;
    description?: string;
  }[];
  outputs?: {
    name: string;
    type: string;
    description?: string;
  }[];
  metrics?: {
    avgCost?: number;
    avgDuration?: number;
    successRate?: number;
    callCount?: number;
  };
  example?: string;
}

/**
 * List all workflow agents for workflow designer palette
 * GET /api/agents/workflow-palette (Agents Gateway)
 */
export async function listWorkflowAgents(): Promise<TimComboAPIResponse<WorkflowAgent>> {
  return Make_Authorized_API_Call<WorkflowAgent>(
    API_ENDPOINTS.AGENTS.WORKFLOW_PALETTE,
    'GET'
  );
}

/**
 * List all capability packs
 * GET /api/agents/capability-packs (Agents Gateway)
 */
export async function listCapabilityPacks(): Promise<TimComboAPIResponse<CapabilityPack>> {
  return Make_Authorized_API_Call<CapabilityPack>(
    API_ENDPOINTS.AGENTS.CAPABILITY_PACKS,
    'GET'
  );
}

/**
 * Get methods for a specific agent
 * GET /api/agents/:agentId/methods (Agents Gateway)
 */
export async function getAgentMethods(agentId: string): Promise<TimComboAPIResponse<AgentMethod>> {
  return Make_Authorized_API_Call<AgentMethod>(
    `${API_ENDPOINTS.AGENTS.METHODS}?agent_id=${encodeURIComponent(agentId)}`,
    'GET'
  );
}
